import { useChatStore } from '@widgets/messenger/stores/useChatStore'
import type {
  MessengerWebSocketEventHandler,
  MessengerWebSocketEventMap,
  MessengerWebSocketEventTypes,
} from '@widgets/messenger/types/api/websocket'
import messengerMessageListItemCastService from '@widgets/messenger/services/messengerMessageListItemCastService'
import { useWidgetChatPopovers } from '@widgets/messenger/compositions/useWidgetChatPopovers'
import { useChatLocalStorage } from '@widgets/messenger/compositions/useChatLocalStorage'
import soundService from '@widgets/shared/services/soundService'
import { useWidgetStore } from '@widgets/messenger/stores/useWidgetStore'
import type { MessengerAutoReplyMessageListItem } from '@widgets/messenger/types/chat/message'
import { useChatScroll } from '@widgets/messenger/compositions/useChatScroll'
import { computed } from '@/composition/vue/compositionApi'
import { WebWidgetChannelType } from '@/data/models/domain/messages/settings/webWidget/types'
import type { HandlerOfAllEvents } from '@/services/transport/types'

export const useWidgetChatEventHandlers = () => {
  const chatStore = useChatStore()
  const { widgetSettings, isActiveTab, isLastActiveTab, currentViewName } = useWidgetStore()
  const chatLocalStorage = useChatLocalStorage()
  const { openSatisfactionSurveyRatePopover } = useWidgetChatPopovers()
  const { scrollToBottom } = useChatScroll()

  const isSatisfactionSurveyShown = computed(
    () => widgetSettings.value.channels[WebWidgetChannelType.CHAT].isSatisfactionSurveyShown,
  )

  const handleMessageSentEvent: MessengerWebSocketEventHandler<'messageSent'> = (event) => {
    if (!event.payload.trackId) {
      return
    }
    const existingMessageListItem = chatStore.findMessageListItem(event.payload.trackId)
    if (existingMessageListItem) {
      chatStore.removeMessageListItem(event.payload.trackId)
    }
    chatStore.addMessageListItem(messengerMessageListItemCastService.castWebSocketSentMessageEvent(event))
  }

  const handleMessageIncomingEvent: MessengerWebSocketEventHandler<'messageIncoming'> = (event) => {
    const isChatView = currentViewName.value === 'chat'
    const messageListItem = messengerMessageListItemCastService.castWebSocketIncomingMessageEvent(event)
    chatStore.addMessageListItem(messageListItem)
    if (isLastActiveTab.value && (!isActiveTab.value || !isChatView)) {
      soundService.playNotificationSound()
    }
    if (chatStore.currentScrollPosition.value.atBottom && isChatView) {
      setTimeout(() => {
        scrollToBottom()
      }, 0)
    } else {
      chatStore.scoreNewMessage(messageListItem.id)
    }
  }

  const handleMessageInitialEvent: MessengerWebSocketEventHandler<'messageInitial'> = (event) => {
    chatStore.addMessageListItem(messengerMessageListItemCastService.castWebSocketInitialMessageEvent(event))
  }

  const handleMessageLogEvent: MessengerWebSocketEventHandler<'messageLog'> = (event) => {
    if (event.payload.type === 'userLeft' && chatStore.chatState.value.type !== 'ended') {
      chatStore.setChatState({
        type: 'waiting',
      })
    }
    const listItem = messengerMessageListItemCastService.castWebSocketLogMessageEvent(event)
    if (listItem) {
      chatStore.addMessageListItem(listItem)
      if (chatStore.currentScrollPosition.value.atBottom) {
        setTimeout(() => {
          scrollToBottom()
        }, 0)
      }
    }
  }

  const handleChatConversationEndedEvent: MessengerWebSocketEventHandler<'chatConversationEnded'> = (event) => {
    const { showSurvey, showAutoReply, showOfflineMessage } = event.payload
    chatStore.setChatState({
      type: 'ended',
      isNoAgents: showAutoReply,
      isOffline: showOfflineMessage,
    })
    chatLocalStorage.remove()
    if (showSurvey && isSatisfactionSurveyShown.value) {
      openSatisfactionSurveyRatePopover()
    }
    if (showAutoReply) {
      const rejectMessageListItem: MessengerAutoReplyMessageListItem = {
        type: 'autoReply',
        id: 'rejectMessage',
        messageTime: new Date().toISOString(),
        text: widgetSettings.value.channels[WebWidgetChannelType.CHAT].rejectMessage,
      }
      chatStore.addMessageListItem(rejectMessageListItem)
    }
    if (showOfflineMessage) {
      const offlineMessageListItem: MessengerAutoReplyMessageListItem = {
        type: 'autoReply',
        id: 'offlineMessage',
        messageTime: new Date().toISOString(),
        text: widgetSettings.value.channels[WebWidgetChannelType.CHAT].uiSettings.offlineMessage,
      }
      chatStore.removeMessageListItem('autoReply')
      chatStore.addMessageListItem(offlineMessageListItem)
    }
    setTimeout(() => {
      scrollToBottom()
    }, 0)
  }

  const messageDeletedHandler: MessengerWebSocketEventHandler<'messageDeleted'> = (event) => {
    if (event.payload.all) {
      chatStore.removeAllMessageListItems()
    } else {
      chatStore.removeMessageListItems(event.payload.ids)
    }
  }

  const typingTimeoutMap = new Map<string, NodeJS.Timeout>()

  const handleTypingEvent: MessengerWebSocketEventHandler<'startTyping' | 'endTyping'> = (event) => {
    if (event.payload.type === 'contactTyping') return
    const userId = event.payload.userId

    if (event.type === 'endTyping') {
      typingTimeoutMap.delete(userId)
      chatStore.setAgentTyping(userId, false)
      return
    }

    if (event.type === 'startTyping') {
      if (typingTimeoutMap.has(userId)) {
        clearTimeout(typingTimeoutMap.get(userId)!)
      }
      chatStore.setAgentTyping(userId, true)
      typingTimeoutMap.set(
        userId,
        setTimeout(() => {
          chatStore.setAgentTyping(userId, false)
        }, 2000),
      )
    }
  }

  const handleLiveChatAgentChanged: MessengerWebSocketEventHandler<'liveChatAgentChanged'> = (event) => {
    chatStore.setChatState({
      type: 'opened',
      agent: {
        name: event.payload.userFullName,
        avatarUrl: event.payload.avatarHref,
      },
    })
  }

  const eventHandlerMap: {
    [key in MessengerWebSocketEventTypes]: MessengerWebSocketEventHandler<key>
  } = {
    messageSent: handleMessageSentEvent,
    messageIncoming: handleMessageIncomingEvent,
    messageInitial: handleMessageInitialEvent,
    messageLog: handleMessageLogEvent,
    chatConversationEnded: handleChatConversationEndedEvent,
    messageDeleted: messageDeletedHandler,
    startTyping: handleTypingEvent,
    endTyping: handleTypingEvent,
    liveChatAgentChanged: handleLiveChatAgentChanged,
  }

  const handleChatEvent: HandlerOfAllEvents<MessengerWebSocketEventMap> = (event) => {
    if (!event || !event.type) {
      return
    }
    const handler = eventHandlerMap[event.type]
    if (handler) {
      // @ts-expect-error there is no error, because we get a right event handler by an event type
      // but ts thinks that every handler should work with every event type,
      // because "handler" is an union and "event" is an union
      handler(event)
    }
  }

  return {
    handleChatEvent,
  }
}
